import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ChevronDown, Plus, Redo2, RotateCw, Trash2, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, Loading } from '../../components/office';
import { useSite } from '../../contexts/SiteContext';
import { floorPlanApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@verlo/shared/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@verlo/shared/components/ui/dialog';
import { FloorFeature, FEATURE_KINDS } from '@verlo/shared/components/floorplan/FloorFeature';
import { start, reduce, planFrom } from '@verlo/shared/lib/floorplanEdit';
import { freeSpot } from '@verlo/shared/lib/floorSpot';

/**
 * Admin › The restaurant › Floor plan (Oct 2026). A site's tables and the room they're in, set up
 * here rather than on the till: the drawing and a table list, editing the same tables. Save
 * sends it to the site's till, which applies it within a minute (backend floor_plans.py).
 * A table someone is sat at keeps its party and changes when it's next free.
 *
 * Back office sweep (Oct 2026): the drawing runs full width; what's selected (or what isn't on
 * the drawing yet) sits in a slim bar above it, beside one Add menu; the area tabs are for
 * looking, and Edit areas renames, removes and adds them.
 */

const nameKey = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();
const SHAPES = [{ value: 'square', label: 'Square' }, { value: 'round', label: 'Round' }, { value: 'rectangle', label: 'Long' }];
const autoWidth = (capacity) => (capacity <= 2 ? 58 : capacity <= 4 ? 68 : capacity <= 6 ? 80 : 94);
const tableBox = (t) => (t.width > 1 && t.height > 1
  ? { width: `${t.width}%`, height: `${t.height}%`, minWidth: 48, minHeight: 36 }
  : { width: `${autoWidth(t.capacity)}px`, height: `${Math.round(autoWidth(t.capacity) * 0.72)}px` });
const placed = (t) => t.position_x > 0 || t.position_y > 0;
const CORNERS = [
  { key: 'nw', cls: '-left-2 -top-2 cursor-nwse-resize', dx: -1, dy: -1 },
  { key: 'ne', cls: '-right-2 -top-2 cursor-nesw-resize', dx: 1, dy: -1 },
  { key: 'sw', cls: '-left-2 -bottom-2 cursor-nesw-resize', dx: -1, dy: 1 },
  { key: 'se', cls: '-right-2 -bottom-2 cursor-nwse-resize', dx: 1, dy: 1 },
];
// The Add menu's first rows, so a wall is easy to find; the rest follow a line.
const MAIN_KINDS = ['wall', 'bar', 'kitchen', 'entrance', 'label'];
const kindLabel = (k) => FEATURE_KINDS.find((x) => x.kind === k)?.label || k;

const AreaSelect = ({ value, areas, onChange }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
    <SelectContent>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
  </Select>
);

// An area's name while editing areas: renamed when you leave the box or press Enter; Escape
// puts the name back and renames nothing (the blur it causes is told to skip).
function AreaName({ name, onRename }) {
  const [v, setV] = useState(name);
  const cancelled = useRef(false);
  useEffect(() => { setV(name); }, [name]);
  const commit = () => {
    if (cancelled.current) { cancelled.current = false; setV(name); return; }
    if (v.trim() && v.trim() !== name) onRename(v); else setV(name);
  };
  return (
    <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} maxLength={30}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur(); } }}
      className="h-8 w-32" aria-label={`Rename ${name}`} data-testid={`floor-area-name-${name}`} />
  );
}

const when = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');

const FloorPlanPage = () => {
  const { site, sites, current } = useSite();
  // The plan is a site's: the one picked in the header, or the only one there is.
  const siteId = site || ((sites || []).length === 1 ? sites[0].id : null);
  const siteName = current?.name || (sites || []).find((s) => s.id === siteId)?.name || '';
  const multi = (sites || []).length > 1;

  const [loaded, setLoaded] = useState(null);         // what the server said: { version, draft, tonight, saved_by_name … }
  const [draft, dispatch] = useReducer(reduce, null, () => start([], []));
  const [areas, setAreas] = useState([]);
  const [savedJson, setSavedJson] = useState('');
  const [section, setSection] = useState('all');
  const [view, setView] = useState('drawing');
  const [saving, setSaving] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);     // the table just added: pulses until touched
  const [adding, setAdding] = useState(null);           // the new table form, while open
  const [removing, setRemoving] = useState(null);       // a table being removed, while asking
  const [removingArea, setRemovingArea] = useState(null);
  const [newArea, setNewArea] = useState(null);
  const [editingAreas, setEditingAreas] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTo, setCopyTo] = useState('');
  const [copyAsk, setCopyAsk] = useState(null);       // the other site has tables: what copying replaces
  const [loadError, setLoadError] = useState(null);
  const planRef = useRef(null);
  // The site on screen now: a load that comes back after the site has changed is dropped, so
  // one site's tables can never land in another's editor (and be saved there).
  const siteNow = useRef(siteId);
  siteNow.current = siteId;

  const load = async () => {
    if (!siteId) return;
    const forSite = siteId;
    setLoadError(null);
    try {
      const { data } = await floorPlanApi.get(forSite);
      if (siteNow.current !== forSite) return;
      dispatch({ type: 'reset', state: start(data.plan.tables || [], data.plan.features || []) });
      setAreas(data.plan.areas?.length ? data.plan.areas : ['Main']);
      setSavedJson(JSON.stringify(planFrom(start(data.plan.tables || [], data.plan.features || []), data.plan.areas || [])));
      setLoaded(data);
      setLastAdded(null);
    } catch (e) {
      if (siteNow.current !== forSite) return;
      setLoadError(e?.response?.data?.detail || "Couldn't load the floor plan");
    }
  };
  // Switching site in the header drops this one's unsaved changes: say so rather than lose them quietly.
  const wasDirty = useRef(false);
  useEffect(() => {
    if (wasDirty.current) toast.warning("The other site's unsaved floor plan changes were dropped.");
    setLoaded(null); load();
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = useMemo(() => planFrom(draft, areas), [draft, areas]);
  const dirty = loaded && (loaded.draft || JSON.stringify(plan) !== savedJson);
  wasDirty.current = !!loaded && !loaded.draft && JSON.stringify(plan) !== savedJson;

  // Undo and redo move tables between areas but not the area list: an area a table is in
  // always has its tab, so undoing "remove Terrace" brings Terrace back. Undoing a rename
  // brings the old name back in the new one's place, rather than beside an empty new one.
  const renamed = useRef({});   // new name → the name it had, for renames not yet undone
  useEffect(() => {
    const used = [...draft.tables, ...draft.features].map((r) => r.section || 'Main');
    const missing = [...new Set(used)].filter((a) => !areas.some((x) => nameKey(x) === nameKey(a)));
    if (!missing.length) return;
    // Worked out here, not in a setAreas updater: React runs an updater twice in development,
    // and the second run would find the rename already used up.
    let next = [...areas];
    for (const m of missing) {
      const was = Object.keys(renamed.current).find((to) => renamed.current[to] === m && !used.includes(to));
      if (was) { next = next.map((a) => (a === was ? m : a)); delete renamed.current[was]; renamed.current[m] = was; } else next.push(m);
    }
    setAreas(next);
  }, [draft.tables, draft.features]); // eslint-disable-line react-hooks/exhaustive-deps

  // What's happening on the tables now, from the till.
  const seated = useMemo(() => Object.fromEntries((loaded?.tonight?.seated || []).map((t) => [t.id, t])), [loaded]);
  const bookings = loaded?.tonight?.bookings || {};

  // Two tables with one name (the server refuses it too; said here first, on the table).
  const dupes = useMemo(() => {
    const seen = {};
    const out = new Set();
    draft.tables.forEach((t) => { const k = nameKey(t.name); if (seen[k]) { out.add(t.id); out.add(seen[k]); } else seen[k] = t.id; });
    return out;
  }, [draft.tables]);

  const save = async () => {
    if (dupes.size) { toast.error('Two tables have the same name. Give each its own before saving.'); return; }
    setSaving(true);
    try {
      const { data } = await floorPlanApi.save(siteId, { ...plan, version: loaded.version || 0 });
      dispatch({ type: 'reset', state: start(data.plan.tables, data.plan.features) });
      setAreas(data.plan.areas);
      setSavedJson(JSON.stringify(planFrom(start(data.plan.tables, data.plan.features), data.plan.areas)));
      setLoaded(data);
      toast.success(`Saved. The till${siteName ? ` at ${siteName}` : ''} picks it up within a minute.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the floor plan");
    } finally {
      setSaving(false);
    }
  };

  // ---------- areas ----------
  const inSection = (row) => section === 'all' || (row.section || 'Main') === section;
  const addArea = () => {
    const name = String(newArea || '').trim().replace(/\s+/g, ' ');
    setNewArea(null);
    if (!name) return;
    const clash = areas.find((a) => nameKey(a) === nameKey(name));
    if (clash) { toast.error(`There's already an area called ${clash}`); return; }
    setAreas([...areas, name]);
    setSection(name);
  };
  const renameArea = (from, to) => {
    const name = String(to || '').trim().replace(/\s+/g, ' ');
    const clash = areas.find((a) => a !== from && nameKey(a) === nameKey(name));
    if (clash) { toast.error(`There's already an area called ${clash}`); return; }
    dispatch({ type: 'moveSection', from, to: name });
    renamed.current[name] = renamed.current[from] || from;
    delete renamed.current[from];
    setAreas(areas.map((a) => (a === from ? name : a)));
    if (section === from) setSection(name);
  };
  const contents = (a) => ({
    tables: draft.tables.filter((t) => (t.section || 'Main') === a).length,
    features: draft.features.filter((f) => (f.section || 'Main') === a).length,
  });
  const askRemoveArea = (a) => {
    const c = contents(a);
    const other = areas.find((x) => x !== a);
    if (!other) { toast.error('A floor needs at least one area. Rename this one instead.'); return; }
    if (!c.tables && !c.features) { setAreas(areas.filter((x) => x !== a)); if (section === a) setSection('all'); return; }
    setRemovingArea({ name: a, into: other });
  };
  const confirmRemoveArea = () => {
    const { name, into } = removingArea;
    dispatch({ type: 'moveSection', from: name, to: into });
    setAreas(areas.filter((x) => x !== name).concat(areas.includes(into) ? [] : [into]));
    if (section === name) setSection(into);
    setRemovingArea(null);
  };

  // ---------- tables ----------
  const selected = draft.selected?.type === 'table' ? draft.tables.find((t) => t.id === draft.selected.id) : null;
  const selectedFeature = draft.selected?.type === 'feature' ? draft.features.find((f) => f.id === draft.selected.id) : null;
  const openAdd = () => setAdding({ name: '', capacity: 4, section: section === 'all' ? (areas[0] || 'Main') : section });
  const addTable = () => {
    const name = adding.name.trim();
    if (!name) { setAdding({ ...adding, tried: true }); return; }
    if (draft.tables.some((t) => nameKey(t.name) === nameKey(name))) return;
    const spot = freeSpot(draft.tables.filter((t) => (t.section || 'Main') === adding.section),
      draft.features.filter((f) => (f.section || 'Main') === adding.section));
    const id = `new-${Date.now().toString(36)}`;
    dispatch({ type: 'addTable', id, name, capacity: Number(adding.capacity) || 2, section: adding.section, position_x: spot.x, position_y: spot.y });
    setLastAdded(id);
    setAdding(null);
    if (!areas.includes(adding.section)) setAreas([...areas, adding.section]);
  };
  const addFeature = (kind) => dispatch({ type: 'addFeature', kind, section: section === 'all' ? (areas[0] || 'Main') : section });
  const touch = (id) => { if (id === lastAdded) setLastAdded(null); };
  const removeTable = (t) => {
    if (seated[t.id] || bookings[t.id]?.length) { setRemoving(t); return; }
    dispatch({ type: 'deleteTable', id: t.id });
  };

  // ---------- gestures: one pointer, one undo step ----------
  const gesture = useRef(null);
  const pct = (e) => {
    const r = planRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 };
  };
  const startGesture = (e, sel, mode, corner) => {
    e.stopPropagation();
    e.preventDefault();
    const item = sel.type === 'feature' ? draft.features.find((f) => f.id === sel.id) : draft.tables.find((t) => t.id === sel.id);
    if (!item) return;
    if (sel.type === 'table') touch(sel.id);
    const r = planRef.current.getBoundingClientRect();
    const box = sel.type === 'feature'
      ? { x: item.x, y: item.y, w: item.w, h: item.h }
      : (() => {
        const w = item.width > 1 ? item.width : (autoWidth(item.capacity) / r.width) * 100;
        const h = item.height > 1 ? item.height : (Math.round(autoWidth(item.capacity) * 0.72) / r.height) * 100;
        return { x: item.position_x - w / 2, y: item.position_y - h / 2, w, h };
      })();
    gesture.current = { sel, mode, corner, origin: pct(e), box, moved: false, from: { tables: draft.tables, features: draft.features, deleted: draft.deleted } };
    dispatch({ type: 'select', sel });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const moveGesture = (e) => {
    const g = gesture.current;
    if (!g) return;
    const p = pct(e);
    const dx = p.x - g.origin.x;
    const dy = p.y - g.origin.y;
    if (Math.abs(dx) < 0.3 && Math.abs(dy) < 0.3 && !g.moved) return;
    g.moved = true;
    let { x, y, w, h } = g.box;
    if (g.mode === 'move') { x += dx; y += dy; } else {
      const c = g.corner;
      if (c.dx < 0) { x += dx; w -= dx; } else { w += dx; }
      if (c.dy < 0) { y += dy; h -= dy; } else { h += dy; }
      w = Math.max(1, w); h = Math.max(1, h);
    }
    const patch = g.sel.type === 'feature'
      ? { x, y, w, h }
      : { position_x: x + w / 2, position_y: y + h / 2, ...(g.mode === 'resize' ? { width: w, height: h } : {}) };
    dispatch({ type: 'preview', sel: g.sel, patch });
  };
  const endGesture = () => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.moved) dispatch({ type: 'commit', from: g.from });
  };

  if (!siteId) {
    return (
      <div className="space-y-6"><PageHeader title="Floor plan" />
        <Notice tone="info" word="Sites" title="Each site has its own floor">Choose a site in the header to see and change its tables.</Notice>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="space-y-6"><PageHeader title="Floor plan" />
        <Notice tone="stop" word="Floor plan" title="Couldn't load the floor plan" action={<Button variant="outline" size="sm" onClick={load}>Try again</Button>}>{loadError}</Notice>
      </div>
    );
  }
  if (!loaded) return <div className="space-y-6"><PageHeader title="Floor plan" /><Loading /></div>;

  const shownTables = draft.tables.filter(inSection);
  const shownFeatures = draft.features.filter(inSection);
  const seatedCount = Object.keys(seated).length;
  const unplaced = shownTables.filter((t) => !placed(t));
  const seats = draft.tables.reduce((n, t) => n + (Number(t.capacity) || 0), 0);
  const field = 'text-[12px] font-medium text-ink-soft';

  return (
    <div className="space-y-5" data-testid="floor-plan-page">
      <PageHeader
        title="Floor plan"
        purpose={<><b className="font-mono font-semibold text-ink" data-testid="floor-count">{draft.tables.length} tables · {seats} seats</b>{siteName ? ` at ${siteName}` : ''}. The till shows this plan and seats from it; changes reach it within a minute.</>}
        actions={(
          <>
            {multi && <Button variant="outline" onClick={() => { setCopyTo(''); setCopyOpen(true); }} data-testid="floor-copy-btn">Copy to another site</Button>}
            {dirty && !loaded.draft && <Button variant="outline" onClick={load} disabled={saving}>Undo changes</Button>}
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={save} disabled={!dirty || saving} data-testid="floor-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      />

      {loaded.draft && (
        <Notice tone="info" word="First save" title="This is your till's floor as it is now">
          Nothing has been set up here yet, so these are the tables your till has. Check them and Save, and from then on the floor is set up here.
        </Notice>
      )}
      {seatedCount > 0 && (
        <Notice tone="warn" word="Service on" data-testid="floor-service-on">
          {seatedCount} {seatedCount === 1 ? 'table is' : 'tables are'} seated now. You can still save: a table someone is sat at keeps its party, and changes to it apply when it's next free.
        </Notice>
      )}

      {/* Areas: for looking, with Edit areas at the end to rename, remove or add one. */}
      <div className="flex flex-wrap items-center gap-3">
        {!editingAreas ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="floor-areas">
            <Segmented value={section} onChange={setSection}
              options={['all', ...areas].map((a) => ({ value: a, label: a === 'all' ? 'All' : a, testId: `floor-area-${a}` }))} />
            <Button variant="ghost" size="sm" className="text-ink-soft" onClick={() => setEditingAreas(true)} data-testid="floor-edit-areas">Edit areas</Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-white p-2" data-testid="floor-areas-editing">
            {areas.map((a) => (
              <span key={a} className="flex items-center gap-1">
                <AreaName name={a} onRename={(to) => renameArea(a, to)} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-ink-faint hover:text-signal-stop" onClick={() => askRemoveArea(a)}
                  aria-label={`Remove the ${a} area`} data-testid={`floor-remove-area-${a}`}><X size={14} /></Button>
              </span>
            ))}
            {newArea != null ? (
              <Input autoFocus value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="e.g. Terrace" className="h-8 w-40"
                onKeyDown={(e) => { if (e.key === 'Enter') addArea(); if (e.key === 'Escape') setNewArea(null); }}
                onBlur={addArea} aria-label="New area name" data-testid="floor-new-area-input" />
            ) : (
              <Button variant="outline" size="sm" onClick={() => setNewArea('')} data-testid="floor-new-area"><Plus size={14} className="mr-1" />Add area</Button>
            )}
            <Button size="sm" className="bg-verlo hover:bg-verlo-hover" onClick={() => { setEditingAreas(false); setNewArea(null); }} data-testid="floor-areas-done">Done</Button>
          </div>
        )}
        <span className="flex-1" />
        <Segmented value={view} onChange={setView}
          options={[{ value: 'drawing', label: 'Drawing', testId: 'floor-view-drawing' }, { value: 'list', label: 'List', testId: 'floor-view-list' }]} />
      </div>

      {removingArea && (
        <Notice tone="warn" word="Remove" title={`Remove ${removingArea.name}?`} data-testid="floor-remove-area-notice"
          action={<div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setRemovingArea(null)}>Keep it</Button>
            <Button size="sm" variant="destructive" onClick={confirmRemoveArea} data-testid="floor-remove-area-confirm">Remove {removingArea.name}</Button>
          </div>}>
          {(() => { const c = contents(removingArea.name); return `${removingArea.name} has ${c.tables} table${c.tables === 1 ? '' : 's'}${c.features ? ` and ${c.features} other thing${c.features === 1 ? '' : 's'}` : ''}. Move them to:`; })()}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {areas.filter((a) => a !== removingArea.name).concat(areas.length < 2 ? ['Main'] : []).map((a) => (
              <button key={a} type="button" onClick={() => setRemovingArea({ ...removingArea, into: a })} aria-pressed={removingArea.into === a}
                className={`h-9 rounded-md border px-3 text-sm ${removingArea.into === a ? 'border-verlo bg-verlo-tint font-semibold text-verlo' : 'border-hairline bg-white text-ink-soft'}`}>{a}</button>
            ))}
          </div>
        </Notice>
      )}

      {/* The slim bar: what's selected, or what isn't on the drawing yet; and adding things. */}
      <div className="flex min-h-[52px] flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-hairline bg-white px-3 py-2" data-testid="floor-bar">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2 text-[13px]">
          {selected ? (
            <>
              <span className="font-heading text-[15px] font-extrabold text-ink" data-testid="floor-table-panel">{selected.name || 'Untitled'}</span>
              {seated[selected.id] && <Signal tone="info" dot={false}>seated now</Signal>}
              <label className="flex items-center gap-1.5"><span className={field}>Name</span>
                <Input value={selected.name} className={`h-8 w-24 ${dupes.has(selected.id) ? 'border-signal-stop-line' : ''}`} maxLength={20}
                  onChange={(e) => dispatch({ type: 'updateTable', id: selected.id, patch: { name: e.target.value } })} data-testid="floor-table-name" /></label>
              <label className="flex items-center gap-1.5"><span className={field}>Seats</span>
                <Input type="number" min={1} max={50} value={selected.capacity} className="h-8 w-16"
                  onChange={(e) => dispatch({ type: 'updateTable', id: selected.id, patch: { capacity: Number(e.target.value) || 1 } })} data-testid="floor-table-seats" /></label>
              <label className="flex items-center gap-1.5"><span className={field}>Area</span>
                <AreaSelect value={selected.section || 'Main'} areas={areas} onChange={(v) => dispatch({ type: 'updateTable', id: selected.id, patch: { section: v } })} /></label>
              <Segmented value={selected.shape || 'square'} onChange={(v) => dispatch({ type: 'updateTable', id: selected.id, patch: { shape: v } })} options={SHAPES} />
              <Button variant="ghost" size="sm" className="text-signal-stop hover:text-signal-stop" onClick={() => removeTable(selected)} data-testid="floor-remove-table">
                <Trash2 size={14} className="mr-1" />Remove</Button>
              {dupes.has(selected.id) && <span className="w-full text-[12px] font-semibold text-signal-stop">Another table has this name</span>}
              {seated[selected.id] && selected.capacity < seated[selected.id].covers && (
                <span className="w-full text-[12px] text-signal-warn">{seated[selected.id].covers} are sat there now and stay; it's {selected.capacity} from the next seating.</span>
              )}
              {(seated[selected.id] || bookings[selected.id]?.length > 0) && (
                <span className="w-full text-[12.5px] text-ink-soft">
                  {seated[selected.id] && `${seated[selected.id].covers} guests since ${when(seated[selected.id].since)}. `}
                  {(bookings[selected.id] || []).slice(0, 3).map((b) => `Booked ${b.time}${b.date !== new Date().toISOString().slice(0, 10) ? ` on ${b.date}` : ''}: ${b.name}, ${b.party_size}`).join(' · ')}
                </span>
              )}
            </>
          ) : selectedFeature ? (
            <>
              <span className="font-heading text-[15px] font-extrabold text-ink">{FEATURE_KINDS.find((k) => k.kind === selectedFeature.kind)?.label || 'Fixture'}</span>
              <label className="flex items-center gap-1.5"><span className={field}>Label</span>
                <Input value={selectedFeature.label || ''} maxLength={40} className="h-8 w-40" onChange={(e) => dispatch({ type: 'rename', id: selectedFeature.id, label: e.target.value })} /></label>
              <label className="flex items-center gap-1.5"><span className={field}>Area</span>
                <AreaSelect value={selectedFeature.section || 'Main'} areas={areas} onChange={(v) => dispatch({ type: 'setSection', sel: { type: 'feature', id: selectedFeature.id }, section: v })} /></label>
              <Button variant="ghost" size="sm" onClick={() => dispatch({ type: 'rotate', id: selectedFeature.id })}><RotateCw size={14} className="mr-1" />Turn</Button>
              <Button variant="ghost" size="sm" className="text-signal-stop hover:text-signal-stop" onClick={() => dispatch({ type: 'deleteFeature', id: selectedFeature.id })}><Trash2 size={14} className="mr-1" />Remove</Button>
            </>
          ) : unplaced.length ? (
            <span className="flex flex-wrap items-center gap-1.5 text-ink-soft" data-testid="floor-unplaced">
              Not on the drawing yet:
              {unplaced.map((t) => (
                <button key={t.id} type="button" className="rounded-md border border-hairline bg-white px-2 py-0.5 font-semibold text-verlo hover:bg-hover-tint"
                  onClick={() => dispatch({ type: 'placeTable', id: t.id, section: section === 'all' ? undefined : section })}
                  data-testid={`floor-place-${t.name}`}>{t.name}</button>
              ))}
              <span className="text-ink-faint">Tap one to put it in the middle, then drag it.</span>
            </span>
          ) : (
            <span className="text-ink-faint">Choose a table or a fixture to change it. Drag to move it; drag a corner to resize.</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="bg-verlo hover:bg-verlo-hover" data-testid="floor-add"><Plus size={14} className="mr-1" />Add<ChevronDown size={14} className="ml-1" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={openAdd} data-testid="floor-add-table">Table</DropdownMenuItem>
              {MAIN_KINDS.map((k) => <DropdownMenuItem key={k} onSelect={() => addFeature(k)} data-testid={`floor-add-${k}`}>{kindLabel(k)}</DropdownMenuItem>)}
              <DropdownMenuSeparator />
              {FEATURE_KINDS.filter((k) => !MAIN_KINDS.includes(k.kind)).map((k) => (
                <DropdownMenuItem key={k.kind} onSelect={() => addFeature(k.kind)} data-testid={`floor-add-${k.kind}`}>{k.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!draft.past.length} onClick={() => dispatch({ type: 'undo' })} aria-label="Undo"><Undo2 size={15} /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!draft.future.length} onClick={() => dispatch({ type: 'redo' })} aria-label="Redo"><Redo2 size={15} /></Button>
        </div>
      </div>

      {view === 'drawing' ? (
        <div className="relative h-[600px] overflow-hidden rounded-lg border border-hairline-strong bg-surface-2"
             style={{ backgroundImage: 'radial-gradient(circle, rgba(32,27,95,0.13) 1px, transparent 1px)', backgroundSize: '2% 2%', touchAction: 'none' }}
             data-testid="floor-drawing">
          <div ref={planRef} className="absolute inset-0" onPointerMove={moveGesture} onPointerUp={endGesture} onPointerCancel={endGesture}
               onPointerDown={() => dispatch({ type: 'select', sel: null })}>
            {shownFeatures.map((f) => {
              const isSel = selectedFeature?.id === f.id;
              return (
                <FloorFeature key={f.id} feature={f} className={`cursor-move ${isSel ? 'ring-2 ring-verlo ring-offset-1' : 'hover:ring-1 hover:ring-verlo/40'}`}
                  onPointerDown={(e) => startGesture(e, { type: 'feature', id: f.id }, 'move')}>
                  {isSel && CORNERS.map((c) => (
                    <span key={c.key} className={`absolute ${c.cls} h-4 w-4 rounded-full border-2 border-verlo bg-white`} style={{ touchAction: 'none' }}
                      onPointerDown={(e) => startGesture(e, { type: 'feature', id: f.id }, 'resize', c)} />
                  ))}
                </FloorFeature>
              );
            })}
            {shownTables.filter(placed).map((t) => {
              const isSel = selected?.id === t.id;
              return (
                <div key={t.id}
                  className={`absolute flex cursor-move flex-col items-center justify-center border-2 bg-white text-center ${t.shape === 'round' ? 'rounded-full' : 'rounded-lg'} ${
                    dupes.has(t.id) ? 'border-signal-stop-line' : seated[t.id] ? 'border-ink-faint bg-surface-2' : 'border-hairline-strong'} ${
                    isSel ? 'ring-2 ring-verlo ring-offset-2' : ''}`}
                  style={{ left: `${t.position_x}%`, top: `${t.position_y}%`, ...tableBox(t), transform: 'translate(-50%, -50%)', zIndex: isSel ? 30 : 20 }}
                  onPointerDown={(e) => startGesture(e, { type: 'table', id: t.id }, 'move')}
                  data-testid={`floor-table-${t.name}`}>
                  {t.id === lastAdded && <span className={`pointer-events-none absolute -inset-1.5 animate-ping border-2 border-verlo-hover ${t.shape === 'round' ? 'rounded-full' : 'rounded-lg'}`} aria-hidden />}
                  <span className="font-heading text-[13px] font-bold leading-none text-ink">{t.name}</span>
                  <span className="mt-0.5 text-[11px] text-ink-faint">{t.capacity}</span>
                  {seated[t.id] && <span className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-white bg-ink" title="Seated now" />}
                  {isSel && CORNERS.map((c) => (
                    <span key={c.key} className={`absolute ${c.cls} h-4 w-4 rounded-full border-2 border-verlo bg-white`} style={{ touchAction: 'none' }}
                      onPointerDown={(e) => startGesture(e, { type: 'table', id: t.id }, 'resize', c)} />
                  ))}
                </div>
              );
            })}
            {section !== 'all' && !shownTables.length && !shownFeatures.length && (
              <div className="pointer-events-none absolute inset-0 grid place-items-center p-6 text-center text-sm text-ink-soft">
                <div><div className="font-heading text-base font-bold text-ink">{section} has no tables yet</div>Add one from Add › Table, or move one here by changing its area.</div>
              </div>
            )}
            {seatedCount > 0 && <div className="absolute bottom-2 right-3 flex items-center gap-1.5 text-[11.5px] text-ink-faint"><span className="h-2.5 w-2.5 rounded-full bg-ink" />seated now</div>}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-hairline bg-white" data-testid="floor-list">
          <table className="w-full min-w-[640px] text-[13.5px]">
            <thead><tr className="border-b border-hairline bg-surface-2 text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
              <th className="px-3 py-2">Table</th><th className="px-3 py-2">Seats</th><th className="px-3 py-2">Area</th><th className="px-3 py-2">Shape</th><th className="px-3 py-2">Now</th><th className="px-3 py-2" /></tr></thead>
            <tbody>
              {shownTables.map((t) => (
                <tr key={t.id} className="border-b border-hairline last:border-0" data-testid={`floor-row-${t.name}`}>
                  <td className="px-3 py-1.5"><Input value={t.name} className={`h-9 w-28 ${dupes.has(t.id) ? 'border-signal-stop-line' : ''}`}
                    onChange={(e) => dispatch({ type: 'updateTable', id: t.id, patch: { name: e.target.value } })} /></td>
                  <td className="px-3 py-1.5"><Input type="number" min={1} max={50} value={t.capacity} className="h-9 w-20"
                    onChange={(e) => dispatch({ type: 'updateTable', id: t.id, patch: { capacity: Number(e.target.value) || 1 } })} /></td>
                  <td className="px-3 py-1.5"><AreaSelect value={t.section || 'Main'} areas={areas} onChange={(v) => dispatch({ type: 'updateTable', id: t.id, patch: { section: v } })} /></td>
                  <td className="px-3 py-1.5"><Segmented value={t.shape || 'square'} onChange={(v) => dispatch({ type: 'updateTable', id: t.id, patch: { shape: v } })} options={SHAPES} /></td>
                  <td className="px-3 py-1.5 text-[12.5px] text-ink-soft">{seated[t.id] ? `${seated[t.id].covers} seated` : bookings[t.id]?.length ? `booked ${bookings[t.id][0].time}` : ''}</td>
                  <td className="px-3 py-1.5 text-right"><Button variant="ghost" size="sm" className="text-signal-stop" onClick={() => removeTable(t)} aria-label={`Remove ${t.name}`}><Trash2 size={14} /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New table */}
      <Dialog open={!!adding} onOpenChange={(v) => { if (!v) setAdding(null); }}>
        <DialogContent size="form" data-testid="floor-add-dialog">
          <DialogHeader><DialogTitle>New table</DialogTitle>
            <DialogDescription>It goes on the drawing in the clearest spot of its area, pulsing until you move it.</DialogDescription></DialogHeader>
          {adding && (() => {
            const clash = adding.name.trim() && draft.tables.find((t) => nameKey(t.name) === nameKey(adding.name));
            return (
              <div className="grid gap-3">
                <label className="grid gap-1"><span className={field}>Name</span>
                  <Input autoFocus value={adding.name} maxLength={20} placeholder="e.g. T12" className={clash || adding.tried ? 'border-signal-stop-line' : ''}
                    onChange={(e) => setAdding({ ...adding, name: e.target.value, tried: false })}
                    onKeyDown={(e) => { if (e.key === 'Enter') addTable(); }} data-testid="floor-add-name" />
                  {clash && <span className="text-[12px] font-semibold text-signal-stop">There's already a table called {clash.name}</span>}
                  {!clash && adding.tried && <span className="text-[12px] font-semibold text-signal-stop" data-testid="floor-add-name-needed">Give the table a name</span>}
                </label>
                <label className="grid gap-1"><span className={field}>Seats</span>
                  <Input type="number" min={1} max={50} value={adding.capacity} onChange={(e) => setAdding({ ...adding, capacity: e.target.value })} /></label>
                <label className="grid gap-1"><span className={field}>Area</span>
                  <AreaSelect value={adding.section} areas={areas} onChange={(v) => setAdding({ ...adding, section: v })} /></label>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAdding(null)}>Cancel</Button>
                  <Button className="bg-verlo hover:bg-verlo-hover" onClick={addTable} data-testid="floor-add-confirm">Add</Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Removing a table with a party or a booking */}
      <Dialog open={!!removing} onOpenChange={(v) => { if (!v) setRemoving(null); }}>
        <DialogContent size="form" data-testid="floor-remove-dialog">
          <DialogHeader><DialogTitle>Remove {removing?.name}?</DialogTitle></DialogHeader>
          {removing && seated[removing.id] && (
            <Notice tone="warn" word="Seated">{removing.name} has {seated[removing.id].covers} guests on it now. It stays on the till until they've left and it's cleared, then it goes.</Notice>
          )}
          {removing && bookings[removing.id]?.length > 0 && (
            <Notice tone="stop" word="Booked">
              {bookings[removing.id].length} booking{bookings[removing.id].length === 1 ? '' : 's'} to come: {bookings[removing.id].slice(0, 3).map((b) => `${b.name}, ${b.party_size} at ${b.time}${b.date !== new Date().toISOString().slice(0, 10) ? ` on ${b.date}` : ''}`).join('; ')}. {bookings[removing.id].length === 1 ? 'It' : 'They'}'ll need another table: pick one in Bookings on the till.
            </Notice>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)}>Keep {removing?.name}</Button>
            <Button variant="destructive" onClick={() => { dispatch({ type: 'deleteTable', id: removing.id }); setRemoving(null); }} data-testid="floor-remove-confirm">
              {seated[removing?.id] ? 'Remove when free' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy to another site */}
      <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
        <DialogContent size="form" data-testid="floor-copy-dialog">
          <DialogHeader><DialogTitle>Copy {siteName}'s floor</DialogTitle>
            <DialogDescription>To another site: its tables, areas and the drawing as saved here. Nothing that's happening at {siteName} comes across.</DialogDescription></DialogHeader>
          <select className="h-10 border border-hairline bg-white px-2 text-sm" value={copyTo} onChange={(e) => { setCopyTo(e.target.value); setCopyAsk(null); }} data-testid="floor-copy-to">
            <option value="">Choose a site</option>
            {(sites || []).filter((s) => s.id !== siteId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {copyTo && <Notice tone="warn" word="Replaces">{copyAsk || "That site's own tables are replaced. Any seated now stay until they're free."}</Notice>}
          {dirty && <p className="text-[12.5px] text-ink-soft">Unsaved changes here aren't copied. Save first to include them.</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" disabled={!copyTo} onClick={async () => {
              try {
                // First without replacing: a site with tables of its own says what would go.
                const { data } = await floorPlanApi.copy(siteId, copyTo, !!copyAsk);
                toast.success(`Copied ${data.tables} tables. Pick that site in the header to adjust them.`);
                setCopyOpen(false);
                setCopyAsk(null);
              } catch (e) {
                const d = e?.response?.data?.detail;
                if (d?.code === 'replace_needed') setCopyAsk(d.message);
                else toast.error((typeof d === 'string' && d) || "Couldn't copy the floor");
              }
            }} data-testid="floor-copy-confirm">{copyAsk ? 'Replace their tables' : 'Copy floor'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FloorPlanPage;
